/**
 * ChannelStackedBarChart · Module B - 横向堆叠柱状图（默认模式）
 *
 * 需求：
 * - 横向柱状图（Y 轴=渠道，X 轴=指标值），各游戏数值按渠道堆叠
 * - 图例 = 游戏（带 icon）
 * - tooltip 展示渠道名称 + 各游戏数值及占比
 * - 渠道数量 > dataZoomThreshold 时启用纵向滚动
 *
 * 参考：
 * - src/pages/opinion/pages/community/video/components/TagAnalysisChart/CommonBarChart.tsx
 *   （横向柱状图 + stack 模式）
 */
import React, { memo, useMemo } from 'react';
import EChartCommon from '@op/components/EChartCommon';
import { formatChartParamsDate } from '@op/components/ChartCommon/formatter';
import { DBO_EC, DBO_V } from '@op/utils/format';
import {
  ChannelIconType,
  channelInfo,
  channelLegendRichInfo,
  dealwithChannelChartKey,
} from '@op/components/ChannelLabel';
import { IChannelChartBarItem } from '../../types/channel';
import { TMetric } from '../../types/common';
import { TGameMap } from '../../hooks/useGameMap';
import { TChannelMap } from '../../hooks/useChannelMap';
import { useGameColorContext } from '../../context/GameColorContext';
import { METRIC_METAS } from '../../constants/metrics';
import {
  buildGameRich,
  gameRichLabel,
  gameTooltipLabel,
} from '../TrendCompare/utils';
import type { IGameSortValue } from './GameSortSelect';
import { ClassSpecial } from '@op/constants/common';

export interface IChannelStackedBarChartProps {
  /** 各渠道的游戏指标 */
  list: IChannelChartBarItem[];
  gameMap: TGameMap;
  /** 渠道元信息映射（id → meta，用于渠道名称翻译） */
  channelMap: TChannelMap;
  metric: TMetric;
  height?: number;
  /** 触发 dataZoom 的渠道数量阈值 */
  dataZoomThreshold?: number;
  emptyTitleText: string;
  loading?: boolean;
  /** 按指定游戏的指标值排序渠道（仅 common-bar 模式生效） */
  sortByGame?: IGameSortValue;
  /** 图例顺序（游戏 ID 列表），仅控制 legend 显示顺序，不影响图表 series 顺序 */
  gameOrder?: string[];
}

const DEFAULT_DATA_ZOOM_THRESHOLD = 8;

/** 使用 Canvas 动态测量 12px Roboto 字体的文本宽度 */
const measureTextWidth = (texts: string[]): number[] => {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) {
    return [];
  }
  context.font = '12px Roboto';
  return texts.map((text) => context.measureText(text).width || 0);
};

const ChannelStackedBarChart = memo(
  ({
    list,
    gameMap,
    channelMap,
    metric,
    height = 380,
    loading,
    dataZoomThreshold = DEFAULT_DATA_ZOOM_THRESHOLD,
    emptyTitleText,
    sortByGame,
    gameOrder,
  }: IChannelStackedBarChartProps) => {
    const { gameColorMap } = useGameColorContext();

    const yFormatter = METRIC_METAS[metric]?.yFormatter;
    const valueFormatter =
      yFormatter === 'percent'
        ? (val: number) =>
            Number.isFinite(val) ? DBO_V.percentageFormat(val * 100) : '-'
        : (val: number) => (Number.isFinite(val) ? DBO_V.reviews(val) : '-');

    const empty = useMemo(() => {
      return (
        !list?.length ||
        list.every(
          (item) => !item.games?.length || item.games.every((g) => !g.value),
        )
      );
    }, [list]);

    const options = useMemo(() => {
      // 1) 排序1：
      //    - sortByGame.by 为非 '__total__' → 按指定游戏指标值排序（asc/desc）
      //    - sortByGame.by 为 '__total__' 或未传 → 按渠道总值排序（asc/desc）
      const isTotalSort = !sortByGame?.by || sortByGame.by === '__total__';
      const sortedList = [...list].sort((a, b) => {
        const getValue = (item: (typeof list)[number]) =>
          isTotalSort
            ? item.games.reduce((sum, g) => sum + (g.value || 0), 0)
            : item.games.find((g) => g.game_id === sortByGame!.by)?.value || 0;

        const valA = getValue(a);
        const valB = getValue(b);
        return sortByGame?.order === 'asc' ? valA - valB : valB - valA;
      });

      // Y 轴：排序后的渠道 ID 列表
      const channelIds = sortedList.map((c) => c.channel_id);

      // 2) 计算每个 game 在所有渠道中的最大值，用于排序2
      const allGameIds = new Set<string>();
      list.forEach((c) => c.games.forEach((g) => allGameIds.add(g.game_id)));

      // 图例顺序：按第一条渠道（sortedList[0]）的游戏值从大到小排列
      const firstChannelValues = new Map<string, number>();
      const topChannel = sortedList[0];
      if (topChannel) {
        topChannel.games.forEach((g) => {
          firstChannelValues.set(g.game_id, g.value || 0);
        });
      }
      const legendData = (
        gameOrder?.length
          ? [...gameOrder].filter((id) => allGameIds.has(id))
          : Array.from(allGameIds)
      ).sort(
        (a, b) =>
          (firstChannelValues.get(b) || 0) - (firstChannelValues.get(a) || 0),
      );

      const gameMaxMap = new Map<string, number>();
      list.forEach((c) =>
        c.games.forEach((g) => {
          const currentMax = gameMaxMap.get(g.game_id) || 0;
          gameMaxMap.set(g.game_id, Math.max(currentMax, g.value || 0));
        }),
      );

      // 排序2：按游戏的最大值由大到小排序，确保每条柱子的堆叠顺序一致
      //    - 若有 sortByGame 且非 total → 选中游戏排最前，其余按最大值排序
      //    - Total 模式 → 所有游戏按最大值排序
      const gameIds = Array.from(allGameIds).sort((a, b) => {
        if (sortByGame?.by && !isTotalSort) {
          // 选中游戏始终排第一（堆叠柱最底部，贴近轴）
          if (a === sortByGame.by) return -1;
          if (b === sortByGame.by) return 1;
        }
        return (gameMaxMap.get(b) || 0) - (gameMaxMap.get(a) || 0);
      });

      // 3) 构造 series：每个 game 一条堆叠柱（stack: 'total'），按排序后的渠道/游戏顺序填值
      const echartSeries = gameIds.map((gameId) => {
        const data = sortedList.map((c) => {
          const found = c.games.find((g) => g.game_id === gameId);
          return found ? found.value : 0;
        });
        const color = gameColorMap[gameId];
        return {
          name: gameId,
          type: 'bar' as const,
          stack: 'total',
          data,
          // 全局统一颜色：覆盖 ECharts 默认色板循环
          ...(color ? { itemStyle: { color } } : {}),
          barMaxWidth: 80,
          label: {
            show: false,
            position: 'right' as const,
            formatter: (params: any) => valueFormatter(params.value),
            fontSize: 12,
          },
        };
      });

      // 4) Legend：游戏 rich 配置
      const gameRich = buildGameRich(
        gameIds.map((id) => ({ game_id: id, values: [] })),
        gameMap,
        16,
      );

      // 5) 渠道名称列表，用于测量文本宽度
      const channelNames = sortedList.map((c) => {
        const channelId = c.channel_id;
        return (
          channelMap[channelId]?.text ||
          channelInfo(channelId as ChannelIconType, undefined, true).name ||
          channelId
        );
      });

      // 计算渠道名称最大宽度（与 opinion 模块保持一致的计算方式）
      const maxLabelWidth =
        Math.max(...(measureTextWidth(channelNames) || [])) || 60;
      const rounded = Math.round(maxLabelWidth / 10) * 10;
      const labelWidth =
        (rounded > maxLabelWidth ? rounded : rounded + 10) + 10;

      // 6) Y 轴渠道图标 rich（左对齐 + 不溢出）
      const channelRich = channelLegendRichInfo(20, 20, false, true);
      Object.keys(channelRich).forEach((key) => {
        channelRich[key] = {
          ...channelRich[key],
          align: 'left',
          overflow: 'none',
        };
      });

      // 7) 是否需要 dataZoom
      const needDataZoom = channelIds.length > dataZoomThreshold;

      // 8) tooltip
      const tooltip: any = {
        enterable: true,
        className: `${ClassSpecial.CHART_TOOLTIP_SCROLL} dbom-ad-comparison-channel-bar-chart-tooltip`,
        formatter: (params: any) => {
          const channelId = params?.[0]?.axisValue as ChannelIconType;
          const { img } = channelInfo(channelId, undefined, true);
          const chName =
            channelMap[channelId]?.text ||
            channelInfo(channelId, undefined, true).name ||
            channelId;
          const iconHtml = img
            ? `<img src="${img}" class="dbom-channel-icon" />`
            : '';
          return formatChartParamsDate({
            title: `${iconHtml}${chName}`,
            params,
            isSort: true,
            listTitleFunc: (item: any) =>
              gameTooltipLabel(item.seriesName, gameMap),
            valueFormat: valueFormatter,
            dealwithRecords: (records: any, total: number) =>
              records.map((item: any) => {
                if (item.originValue === 0 || yFormatter === 'percent')
                  return item;
                return {
                  ...item,
                  value: `${item.value}(${DBO_V.percentage(
                    item.originValue,
                    total,
                    2,
                  )})`,
                };
              }),
          });
        },
      };

      const baseOptions: any = {
        tooltip,
        series: echartSeries,
        xAxis: {
          type: 'value',
          boundaryGap: '0%',
          ...(yFormatter === 'percent' ? { max: 1 } : {}),
          axisLabel: {
            formatter: (value: number) =>
              yFormatter === 'percent'
                ? DBO_EC.percent(value, false)
                : DBO_EC.reviews(value),
            color: '#838697',
            fontSize: 12,
          },
          axisLine: {
            show: true,
            lineStyle: { color: '#E5E5E5', type: 'solid' },
          },
          splitLine: {
            show: true,
            lineStyle: { color: '#E5E5E5', type: 'dashed' },
          },
        },
        yAxis: [
          {
            type: 'category',
            position: 'left',
            data: channelIds,
            inverse: true,
            axisLabel: {
              interval: 0,
              show: true,
              formatter: (cid: string) => {
                const richKey = dealwithChannelChartKey(cid);
                const iconKey = channelRich[richKey]
                  ? richKey
                  : dealwithChannelChartKey('default');
                return `{${iconKey}|}`;
              },
              rich: channelRich,
              width: 20,
              align: 'right',
              padding: [0, labelWidth + 6, 0, 0],
              height: 20,
              verticalAlign: 'middle',
            },
            splitLine: { show: false },
            axisLine: { show: false },
            axisTick: { show: false },
          },
          {
            type: 'category',
            position: 'left',
            data: channelIds,
            inverse: true,
            axisLabel: {
              interval: 0,
              show: true,
              formatter: (cid: string) => {
                const chName =
                  channelMap[cid]?.text ||
                  channelInfo(cid as ChannelIconType, undefined, true).name ||
                  cid;
                return `{label|${chName}}`;
              },
              rich: {
                label: {
                  align: 'left',
                },
              },
              padding: [0, 0, 0, 0],
              overflow: 'break',
              fontWeight: 400,
              fontSize: 12,
              lineHeight: 20,
              width: labelWidth + 4,
            },
            splitLine: { show: false },
            axisLine: { show: false },
            axisTick: { show: false },
          },
        ],
        legend: {
          show: true,
          type: 'scroll' as const,
          data: legendData,
          formatter: (gameId: string) => gameRichLabel(gameId, gameMap),
          textStyle: { rich: gameRich },
        },
        grid: {
          left: 0,
          right: 20,
          top: 10,
          contentLabel: true,
        },
      };

      // dataZoom：同时作用于两个 yAxis，确保图标和文案同步滚动
      if (needDataZoom) {
        baseOptions.dataZoom = [
          {
            type: 'inside',
            yAxisIndex: [0, 1],
            filterMode: 'none',
            maxValueSpan: dataZoomThreshold - 1,
            minValueSpan: dataZoomThreshold - 1,
            startValue: 0,
            endValue: dataZoomThreshold - 1,
            moveOnMouseWheel: true,
            zoomOnMouseWheel: false,
          },
          {
            type: 'slider',
            filterMode: 'none',
            show: true,
            yAxisIndex: [0, 1],
            width: 0,
            maxValueSpan: dataZoomThreshold - 1,
            minValueSpan: dataZoomThreshold - 1,
            startValue: 0,
            endValue: 100,
            right: 10,
            showDetail: false,
            showDataShadow: false,
            moveHandleSize: 8,
            moveHandleIcon: 'none',
            zoomLock: true,
            borderColor: 'rgba(0,0,0,0)',
            moveHandleStyle: { color: '#D8DAE4', borderCap: 'square' },
            emphasis: { moveHandleStyle: { color: '#D8DAE4' } },
          },
        ];
      }

      return baseOptions;
    }, [
      list,
      dataZoomThreshold,
      gameMap,
      gameColorMap,
      metric,
      sortByGame,
      gameOrder,
    ]);

    return (
      <EChartCommon
        options={options}
        optionsProps={{ type: 'bar', excludeOptions: ['axisPointer'] }}
        height={height}
        emptyTitleText={emptyTitleText}
        empty={empty}
        loading={loading}
        hasEmptyDesc={false}
      />
    );
  },
);

ChannelStackedBarChart.displayName = 'ChannelStackedBarChart';

export default ChannelStackedBarChart;
